import { AfterViewInit, Component, Inject, Input, NgModule, OnDestroy } from '@angular/core'
import { AdTargeting, SlotID } from '../advertising-service/ad-config'
import { DISPLAY_ADVERTISING_SERVICE } from '../advertising-service/advertising.token'
import { DisplayAdvertisingService } from '../advertising-service/display-advertising-service.interface'
import { LOGGER_SERVICE } from '../../../logger/logger.config'
import { LoggerService } from '../../../logger/logger.interface'
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  interval,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  timer
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AdComponentConfig, AdComponentInputs } from './ad-component.interface'
import { Slot } from '../advertising-service/gpt.service'
import { CommonModule } from '@angular/common'
import { environment } from '../../../../environments/environment'
import { FullscreenStatusService } from '../../../../../projects/fullscreen-detector/src/lib/fullscreen-status.service'
import { fst } from 'fp-ts/Tuple'
import { SegmentService } from '../../analytics/services/segment.service'
import { SegmentEvents } from '../../analytics/models/segment-events.model'
import { SegmentTrackDisplayAds } from '../../interfaces/segment-track-display-ads.interface'
import { createTrackDisplayAdProperties } from '../ads.utility'

/**
 * The minimum percentage of an ad which must be visible for it to refresh.
 *
 * @see SlotVisibilityChangedEvent
 */
const MINIMUM_REFRESH_VIEWABLE_PERCENTAGE = 49

/**
 * @TODO: Inject Math for test mocking
 */
const getRandomPostfix = () => {
  return `-${Math.random().toFixed(10).split('.')[1]}`
}

const updateSlotTargeting =
  (targets: AdTargeting) =>
  (slot: Slot): void => {
    slot.updateTargetingFromMap({
      ad_ids: targets?.contentIds?.map(String) ?? [],
      ad_categories: targets?.categorySlugs ?? []
    })

    if (targets?.isPremium) {
      slot.setTargeting('user_is_pro', String(targets.isPremium))
    }
  }

@Component({
  template: '<div [id]="slotId" [ngClass]="{ \'pointer-events-none\': preventClick$ | async }"></div>'
})
export class AdComponent implements AfterViewInit, OnDestroy, AdComponentInputs {
  /**
   * Pass the config to use for ad loading.
   *
   * Be aware that the component currently does not support updating or changing
   * configs. The first config passed will be used to create and load a slot.
   */
  @Input() set config(deps: AdComponentConfig) {
    // @TODO: Let's investigate monocle-ts as a solution for this kind of configuration merging/updating.
    const withRandomizedSlotId: AdComponentConfig = {
      ...deps,
      adConfig: {
        ...deps.adConfig,
        adSlotId: deps.adConfig.adSlotId + getRandomPostfix()
      },
      additionalOpts: {
        ...deps.additionalOpts,
        maxRefreshTime: deps.additionalOpts?.maxRefreshTime ?? environment.advertising.maxRefreshTime
      }
    }
    this.logger.trace('AdComponent Config Set', { config: withRandomizedSlotId })

    if (deps?.additionalOpts?.deferClick && deps.additionalOpts.deferClick > 0) {
      this.logger.trace('AdComponent Preventing Click', { config: withRandomizedSlotId })
      this.preventClick$.next(true)
    }

    this.deps.next(withRandomizedSlotId)
  }

  /**
   * Disables users ability to click on an ad
   */
  public preventClick$ = new BehaviorSubject(false)

  public slotId: SlotID

  /** Should emit during ngOnDestroy. */
  private destroy = new Subject<void>()

  /** This subject should be nexted once during ngAfterViewInit. */
  private viewInit = new ReplaySubject<void>(1)

  /** Use this to check if the view for the component has been initialized. */
  private viewInit$ = this.viewInit.pipe(take(1), shareReplay(1))

  /** Pass each inputted config for this component into this subject. */
  private deps = new ReplaySubject<AdComponentConfig>(1)

  /** Performs some actions to prepare the template before providing the deps. */
  private deps$ = this.deps.pipe(
    take(1), // We don't support reconfiguration yet.
    tap(deps => {
      this.slotId = deps.adConfig.adSlotId
      this.logger.trace('AdComponent Set SlotID', {
        slotId: this.slotId
      })
    }),
    shareReplay(1)
  )

  /**
   * Obtain the reference to the GPT Slot related to the ad.
   *
   * This will probably be unused most of the time, but can be helpful for
   * updating targeting, and is currently necessary for synchronizing tests.
   *
   * deferClick, if defined, sets clickTimeout$ to true elsewhere. Here, if
   * defined, it will next it to true after given milliseconds.
   */
  public slot$ = combineLatest([this.deps$, this.viewInit$]).pipe(
    map(([deps]) => deps),
    tap(config => {
      if (config?.additionalOpts?.deferClick && config.additionalOpts.deferClick > 0) {
        timer(config.additionalOpts.deferClick).subscribe(() => {
          this.preventClick$.next(false)
          this.logger.trace('AdComponent Allowing Clicks', { config })
        })
      }
    }),
    map(deps => deps.adConfig),
    switchMap(conf => {
      return this.adService.loadAd(conf).pipe(take(1))
    }),
    tap(slot => {
      this.logger.trace('AdComponent Received Slot', {
        slotId: slot.getSlotElementId()
      })
    }),
    takeUntil(this.destroy),
    shareReplay(1)
  )

  private readonly slotViewable$ = this.slot$.pipe(
    take(1),
    mergeMap(s => this.adService.getSlotVisibilityChanges(s)),
    map(
      e =>
        e.inViewPercentage > (environment.advertising.minimumRefreshVisibility ?? MINIMUM_REFRESH_VIEWABLE_PERCENTAGE)
    ),
    distinctUntilChanged(),
    takeUntil(this.destroy)
  )

  // prettier-ignore
  private readonly isRefreshable$: Observable<boolean> = combineLatest([
    this.slotViewable$,
    this.fullscreenStatus.isFullscreen$
  ]).pipe(
    map(([isViewable, isFullscreen]) => isViewable && !isFullscreen),
    distinctUntilChanged(),
    withLatestFrom(this.slot$),
    tap(([viewable, slot]) => {
      this.logger.trace('Ad Viewability Changed', {
        slot: slot.getSlotElementId(),
        viewable
      })
    }),
    map(fst),
    shareReplay(1),
    takeUntil(this.destroy)
  )

  /** Analytics */
  private readonly slotRenderEndedEvent$ = this.slot$.pipe(
    mergeMap(s => this.adService.getSlotRenderEndedEvent(s)),
    distinctUntilChanged()
  )

  private readonly adViewed$ = combineLatest([this.slotRenderEndedEvent$, this.deps$, this.isRefreshable$])
    .pipe(
      tap(([slotRender, deps, isRefreshable]) => {
        const context: SegmentTrackDisplayAds = createTrackDisplayAdProperties(
          slotRender,
          deps.adConfig.targeting?.analyticsTracking,
          isRefreshable
        )
        // Ad is only refreshable if it is visible to the user
        if (isRefreshable) {
          this.segmentService.trackDisplayAd(context, SegmentEvents.AD_VIEWED)
        }
      }),
      distinctUntilChanged(),
      takeUntil(this.destroy)
    )
    .subscribe()

  // prettier-ignore
  private readonly changesSubscription: Subscription = this.isRefreshable$.pipe(
    takeUntil(this.destroy)
  ).subscribe()

  // prettier-ignore
  private readonly slotSubscription: Subscription = this.slot$.pipe(
    takeUntil(this.destroy)
  ).subscribe()

  // prettier-ignore
  public constructor(
    @Inject(DISPLAY_ADVERTISING_SERVICE) private readonly adService: DisplayAdvertisingService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly fullscreenStatus: FullscreenStatusService,
    private readonly segmentService: SegmentService
  ) {}

  /**
   * Refresh the ad, obtaining a new creative to display.
   *
   * The returned observable is cold, and only emits once. Refreshing ads costs
   * us money, so rate-limit refreshing as much as sensible.
   */
  public refresh(): Observable<void> {
    return this.slot$.pipe(
      withLatestFrom(this.isRefreshable$),
      filter(([_, isRefreshable]) => isRefreshable),
      map(([s]) => s),
      mergeMap(slot => this.adService.refreshBids([slot])),
      take(1)
    )
  }

  /**
   * Update the targeting for this ad.
   *
   * Note that this method _DOES NOT_ refresh the ad. This is to allow for
   * rate-limiting refresh requests, or otherwise updating the targeting
   * independently of refreshes. The longer you can wait between targeting
   * updates and refreshes, the more opportunity there is for advertisers to
   * bid... I think.
   *
   * Additionally, be aware that this method does not reset the targeting. That
   * functionality would have to be managed separately to ensure amazon bidding
   * is reattached after the reset. Avoid the need to reset the targeting however
   * possible, as this would be somewhat complex logically.
   */
  public updateTargeting(targets: AdTargeting): Observable<void> {
    // prettier-ignore
    return this.slot$.pipe(
      map(updateSlotTargeting(targets)),
      take(1),
    )
  }

  public ngAfterViewInit() {
    this.logger.trace('AdComponent View Initialized')
    this.viewInit.next()
  }

  public ngOnDestroy() {
    this.destroy.next()

    // prettier-ignore
    this.slot$.pipe(
      mergeMap(slot => this.adService.destroySlot(slot))
    ).subscribe(destroyed => {
      if (destroyed) {
        this.logger.trace('AdComponent Destroyed Its Slot', {
          slotId: this.slotId
        })
      }
    })
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [AdComponent]
})
class AdModule {}
